import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

type UserRole = 'admin' | 'vendedor' | 'member' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isVendedor: boolean;
  userRole: UserRole;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  isVendedor: false,
  userRole: null,
  signOut: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVendedor, setIsVendedor] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);

  // Avoid re-checking role on token refresh events
  const roleCheckedForUserIdRef = useRef<string | null>(null);

  const checkUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        if (import.meta.env.DEV) {
          console.error('Error checking user role:', error);
        }
        setIsAdmin(false);
        setIsVendedor(false);
        setUserRole(null);
        return;
      }

      const roles = data?.map(r => r.role) || [];
      const hasAdmin = roles.includes('admin');
      const hasVendedor = roles.includes('vendedor');

      setIsAdmin(hasAdmin);
      setIsVendedor(hasVendedor);

      if (hasAdmin) {
        setUserRole('admin');
      } else if (hasVendedor) {
        setUserRole('vendedor');
      } else if (roles.includes('member')) {
        setUserRole('member');
      } else {
        setUserRole(null);
      }

      roleCheckedForUserIdRef.current = userId;
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error checking user role:', err);
      }
      setIsAdmin(false);
      setIsVendedor(false);
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const userId = session.user.id;
        const alreadyChecked = roleCheckedForUserIdRef.current === userId;

        if (alreadyChecked) {
          setLoading(false);
          return;
        }

        // Keep loading true until role is checked to prevent redirect loops
        setLoading(true);
        setTimeout(() => {
          checkUserRole(userId);
        }, 0);
      } else {
        roleCheckedForUserIdRef.current = null;
        setIsAdmin(false);
        setIsVendedor(false);
        setUserRole(null);
        setLoading(false);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const userId = session.user.id;
        const alreadyChecked = roleCheckedForUserIdRef.current === userId;

        if (alreadyChecked) {
          setLoading(false);
          return;
        }

        setLoading(true);
        checkUserRole(userId);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    roleCheckedForUserIdRef.current = null;
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsVendedor(false);
    setUserRole(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, isVendedor, userRole, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
